import { Injectable } from '@angular/core';

import { WorkspaceInterface } from '@reach/interfaces/workspaces/workspace.interface';
import {
	WorkspaceCreateBody,
	WorkspaceUpdateBody,
} from '@reach/interfaces/workspaces/workspace.requests';
import {
	CreateWorkspaceResponse,
	DeleteWorkspaceResponse,
	GetWorkspacesResponse,
	IncludeNodesIntoWorkspaceResponse,
	RemoveNodesFromWorkspaceResponse,
	UpdateLastSearchedResponse,
	UpdateWorkspaceResponse,
} from '@reach/interfaces/workspaces/workspaces.responses';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';
import { asWorkspaceId, WorkspaceId } from '~app-client/workspaces/types';

type ClientWorkspaceResponse = Omit<WorkspaceInterface, 'id'> & { id: WorkspaceId };

@Injectable({ providedIn: 'root' })
export class WorkspacesApiService extends BaseApiService {
	protected commonPath = ['workspaces'];

	public async getAll(): Promise<GetWorkspacesResponse> {
		const params = { t: this.paramTime };
		return await this.http.get<GetWorkspacesResponse>(this.url(), params);
	}

	public async moveNodes(
		destinyWorkspaceId: WorkspaceId,
		...nodeIds: ReachNodeId[]
	): Promise<void> {
		const body = { nodeIds: nodeIds || [] };
		return await this.http.post<void>(this.url(destinyWorkspaceId, 'move'), body);
	}

	public async create(body: WorkspaceCreateBody): Promise<ClientWorkspaceResponse> {
		const response = await this.http.post<CreateWorkspaceResponse>(this.url(), body);
		return this.brandWorkspace(response);
	}

	public async addNodesToWorkspace(
		workspaceId: WorkspaceId,
		nodeIds: ReachNodeId[]
	): Promise<IncludeNodesIntoWorkspaceResponse> {
		const body = { nodeIds };
		return await this.http.post<IncludeNodesIntoWorkspaceResponse>(
			this.url(workspaceId, 'add'),
			body
		);
	}

	public async removeNodesFromWorkspace(
		workspaceId: WorkspaceId,
		nodeIds: ReachNodeId[]
	): Promise<RemoveNodesFromWorkspaceResponse> {
		const body = { nodeIds };
		return await this.http.post<RemoveNodesFromWorkspaceResponse>(
			this.url(workspaceId, 'remove'),
			body
		);
	}

	public async deleteWorkspace(workspaceId: WorkspaceId): Promise<DeleteWorkspaceResponse> {
		return await this.http.delete<DeleteWorkspaceResponse>(this.url(workspaceId));
	}

	public async edit(
		workspaceId: WorkspaceId,
		data: WorkspaceUpdateBody
	): Promise<ClientWorkspaceResponse> {
		const response = await this.http.patch<UpdateWorkspaceResponse>(
			this.url(workspaceId),
			data
		);
		return this.brandWorkspace(response);
	}

	private brandWorkspace(workspace: WorkspaceInterface): ClientWorkspaceResponse {
		return {
			...workspace,
			id: asWorkspaceId(workspace.id),
		};
	}

	public async setLastUsedWorkspaces(ids: WorkspaceId[]): Promise<UpdateLastSearchedResponse> {
		const body = { workspaceIds: ids };
		return await this.http.patch<UpdateLastSearchedResponse>(
			this.url('update-last-searched'),
			body
		);
	}
}
